import css from './default-printers-section-css.js';
import { layoutHtml } from './layout-html.js';
import { sendNotificationEvent } from '../../../appShell/notificationHelpers';

class DefaultPrintersSection extends HTMLElement {
  constructor() {
    super();
    this.shadow = this.attachShadow({ mode: 'open' });
    this.translationService = { translate: (word) => { return word; } };
    this.controlState = {};

    // Component state
    this._state = {
      user: {},
      sites: [],
      siteGroups: [],
      deviceOptions: {
        defaultPrinterId: {},
        defaultReceiptPrinterId: {},
        defaultAtjPrinterId: {},
      },
      siteSettings: [],
      isDataDirty: false
    };

    this.requestServices(
      [
        ['translationService', this.setTranslationService],
        ['controlState', this.onControlStateChange],
      ],
    );

    // Build component
    this.build();
  }

  // Sets translation service.
  setTranslationService = (instance) => {
    this.translationService = instance;
  }

  requestServices = (services) => {
    services.forEach(serviceDef => {
      const requestServiceEvent = new CustomEvent('requestService', {
        bubbles: true,
        composed: true,
        detail: {
          name: serviceDef[0],
          callback: serviceDef[1],
        },
      });
      window.dispatchEvent(requestServiceEvent);
    });
  }

  onControlStateChange = (controlState) => {
    this.controlState = controlState;
    if (controlState.lastAction === 'languageChanged') {
      this.render();
    }
    if (controlState.accessToken && this.store) {
      this.store.accessToken = controlState.accessToken;
    }
  }

  // Set services from parent (keeping for backward compatibility)
  setServices(controlState, translationService) {
    this.controlState = controlState;
    this.translationService = translationService;
  }

  // Initialize component with data
  init(store, user, sites, siteGroups, deleteManager, addDiscardConfirmationDialog, removeConfirmationDialog) {
    this.store = store;
    this._state.user = user;
    this._state.sites = sites;
    this._state.siteGroups = siteGroups;
    this.deleteManager = deleteManager;
    this.addDiscardConfirmationDialog = addDiscardConfirmationDialog;
    this.removeConfirmationDialog = removeConfirmationDialog;

    // Fetch user data first, then device options and site settings
    this.fetchUserData();
  }

  // Fetch user data including site settings
  async fetchUserData() {
    try {
      const data = { userId: this._state.user.id };
      const userData = await this.store.sendCrudRequest(this.controlState.chainId, 'GetUser', data);
      this.updateUserData(userData);

      // After getting user data, fetch device options and setup site settings
      await this.fetchDeviceOptions();
      this.setupSiteSettings();
      this.render();
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      sendNotificationEvent(this.translationService.translate('usersView.fetchFailed'), 'error');
    }
  }

  // Update user data with fetched information
  updateUserData(userData) {
    if (userData) {
      // Update the user object with complete data including site settings
      this._state.user = {
        ...this._state.user,
        ...userData,
        siteSettings: userData.siteSettings || []
      };
    }
  }
  // Fetch available devices for printers
  async fetchDeviceOptions() {
    try {
      const chainId = this.controlState.chainId;
      const userId = this._state.user.userId;
      const result = await this.store.getAvailableUserDevices(chainId, userId);

      const printers = result.filter(device => device.type === 'generalPrinter');
      const receiptPrinters = result.filter(device => device.type === 'receiptPrinter');
      const atjPrinters = result.filter(device => device.type === 'atjPrinter');

      // Organize devices per site - use updated user data
      const siteIds = this._state.user.userType === 'superuser'
        ? this._state.sites.map(site => site.id)
        : this._state.user.sites || [];

      const deviceOptions = {
        defaultPrinterId: {},
        defaultReceiptPrinterId: {},
        defaultAtjPrinterId: {},
      };

      siteIds.forEach(siteId => {
        deviceOptions.defaultPrinterId[siteId] = printers.filter(device => device.siteId === siteId);
        deviceOptions.defaultReceiptPrinterId[siteId] = receiptPrinters.filter(device => device.siteId === siteId);
        deviceOptions.defaultAtjPrinterId[siteId] = atjPrinters.filter(device => device.siteId === siteId);
      });

      this._state.deviceOptions = deviceOptions;
    } catch (error) {
      console.error('Failed to fetch device options:', error);
    }
  }

  // Setup site settings from user data
  setupSiteSettings() {
    const siteIds = this._state.user.userType === 'superuser'
      ? this._state.sites.map(site => site.id)
      : this._state.user.sites || [];

    const siteSettings = siteIds.map(siteId => {
      const existingSetting = this._state.user.siteSettings?.find(setting => setting.siteId === siteId);
      return existingSetting || {
        siteId,
        defaultPrinterId: '',
        defaultReceiptPrinterId: '',
        defaultAtjPrinterId: '',
      };
    });

    this._state.siteSettings = siteSettings;
  }

  // Handle printer selection changes
  handlePrinterChange = (siteId, printerType) => (event) => {
    const value = Number(event.target.value || '-1');
    const index = this._state.siteSettings.findIndex(setting => setting.siteId === siteId);

    if (index !== -1 && value !== -1) {
      const newSetting = { ...this._state.siteSettings[index], [printerType]: value };
      const newSiteSettings = [...this._state.siteSettings];
      newSiteSettings[index] = newSetting;

      this._state.siteSettings = newSiteSettings;
      this._state.isDataDirty = true;
      this.render();
    }
  };

  // Save button handler
  onSave = async () => {
    try {
      const data = {
        userId: this._state.user.userId,
        firstName: this._state.user.firstName,
        lastName: this._state.user.lastName,
        preferredLanguage: this._state.user.preferredLanguage,
        sites: this._state.user.sites,
        siteGroups: this._state.user.siteGroups,
        siteSettings: JSON.stringify(this._state.siteSettings),
        permissions: this._state.user.permissions,
        lastSiteId: this._state.user.lastSiteId,
      };

      const userData = await this.store.sendCrudRequest(this.controlState.chainId, 'UpdateUser', data);
      this.updateUserData(userData);
      this._state.isDataDirty = false;
      this.render();
      sendNotificationEvent(this.translationService.translate('chainView.updateSuccessful'));
    } catch (error) {
      console.error('Failed to save printer settings:', error);
      sendNotificationEvent(this.translationService.translate('chainView.updateFailed'), 'error');
    }
  };

  // Cancel button handler
  onCancel = () => {
    const navEvent = new CustomEvent('usersNavEvent', {
      bubbles: true,
      composed: true,
      detail: {
        route: '/users-list',
        data: {}
      },
    });
    this.shadow.dispatchEvent(navEvent);
  };

  // Get site name by ID
  getSiteName(siteId) {
    const site = this._state.sites.find(s => s.id === siteId);
    return site?.name || '';
  }

  // Build component structure
  build() {
    this.shadow.innerHTML = '<style type="text/css" media="screen">' + css + '</style>';
    this.root = document.createElement('div');
    this.root.setAttribute('id', 'default-printers-section');
    this.root.setAttribute('class', 'default-printers-section');
    this.shadow.appendChild(this.root);
  }

  // Render component
  render() {
    if (!this.root) return;

    // Generate HTML content using layoutHtml
    this.root.innerHTML = layoutHtml(
      this._state,
      this.translationService,
      this.getSiteName.bind(this)
    );

    // Add event listeners after rendering
    this.addEventListeners();
  }

  // Add event listeners to rendered elements
  addEventListeners() {
    // Initialize buttons with text and event handlers
    const saveButton = this.root.querySelector('#button-save');
    if (saveButton) {
      saveButton.textContent = '';
      saveButton.appendChild(document.createTextNode(this.translationService.translate('save')));
      saveButton.onclick = this.onSave;
    }

    const cancelButton = this.root.querySelector('#button-cancel');
    if (cancelButton) {
      cancelButton.textContent = '';
      cancelButton.appendChild(document.createTextNode(this.translationService.translate('cancel')));
      cancelButton.onclick = this.onCancel;
    }

    const deleteUserButton = this.root.querySelector('#button-delete-user');
    if (deleteUserButton) {
      deleteUserButton.textContent = '';
      deleteUserButton.appendChild(document.createTextNode(this.translationService.translate('usersView.delete')));
      deleteUserButton.onclick = this.onDeleteUser;
    }

    // Add event listeners for printer selects
    const selects = this.root.querySelectorAll('select');
    selects.forEach(select => {
      const siteId = parseInt(select.getAttribute('data-site-id'));
      const printerType = select.getAttribute('data-printer-type');

      if (siteId && printerType) {
        select.addEventListener('change', this.handlePrinterChange(siteId, printerType));
      }
    });
  }

  // Get current state for saving
  getState() {
    return {
      siteSettings: this._state.siteSettings,
      isDataDirty: this._state.isDataDirty
    };
  }

  // Reset dirty state
  resetDirtyState() {
    this._state.isDataDirty = false;
  }
}

window.customElements.define('user-default-printers-section', DefaultPrintersSection);